/**
 * Recording attendance for one activity (M1 track D6).
 *
 * Sits below the roster on the activity screen, because recording hours is done
 * while looking at who was expected: the picker offers the assigned volunteers
 * first, and anyone else second, with the warning that Plan D3 requires.
 *
 * **Recorded is counted.** Hours entered here are approved as they are saved
 * (Parinaam, 8 Oct 2026); there is no second step and no queue to send them to.
 * This panel used to end with a line telling staff how many rows were waiting
 * for somebody else, which is now always none.
 *
 * **The Plan D3 flow, in full.** Recording for someone with no assignment is not
 * refused — the person did the work. The first request omits `allow_unassigned`,
 * so the API answers 409 `ASSIGNMENT_REQUIRED`; this panel shows what happened
 * and offers "record it anyway", which retries with the flag set and produces an
 * `ATTENDANCE_WITHOUT_ASSIGNMENT` audit row. The screen branches on
 * `error.code`, never on the message text.
 *
 * **Plan D4 arrives the same way** and can arrive *as well*: a record that puts
 * the volunteer somewhere she is already recorded as having been is refused once
 * with `OVERLAP_DETECTED`. One record can raise both questions, so they are
 * collected rather than asked in sequence — see `useConfirmations`. Staff read
 * everything that is wrong, and answer once.
 */

import { useState } from 'react';
import { ACTIVITY_STATUS, isGroupCategory } from '@parinaam-vms/shared';
import type { ActivityWithProgram } from '@parinaam-vms/shared';
import {
  Button,
  Card,
  ConfirmWarning,
  DurationInput,
  EmptyState,
  ErrorState,
  LoadingState,
  SelectInput,
  TextInput,
} from '../../components/common';
import { AttendanceTable } from '../../components/attendance/AttendanceTable';
import { useActivityAssignments } from '../../hooks/useAssignments';
import { useActivityAttendance, useCreateAttendance } from '../../hooks/useAttendance';
import { useConfirmations } from '../../hooks/useConfirmations';
import { useVolunteers } from '../../hooks/useVolunteers';
import type { CreateAttendanceBody } from '../../services/api/attendance';
import styles from './AttendancePanel.module.css';
import { useActivitySessions } from '../../hooks/useSessions';
import { sessionLabel } from '../../services/api/sessions';

export interface AttendancePanelProps {
  activity: ActivityWithProgram;
}

export function AttendancePanel({ activity }: AttendancePanelProps) {
  const [recording, setRecording] = useState(false);

  const attendance = useActivityAttendance(activity.activity_id);
  const rows = attendance.data ?? [];

  return (
    <Card
      title={`Attendance${rows.length > 0 ? ` (${rows.length})` : ''}`}
      action={
        !recording &&
        activity.status !== ACTIVITY_STATUS.CANCELLED &&
        // A deleted activity refuses new records at the API, and anything
        // written against it would be excluded from every figure anyway.
        activity.status !== ACTIVITY_STATUS.DELETED && (
          <Button variant="primary" onClick={() => setRecording(true)}>
            Record attendance
          </Button>
        )
      }
    >
      {recording && <RecordForm activity={activity} onClose={() => setRecording(false)} />}

      {attendance.isPending ? (
        <LoadingState />
      ) : attendance.isError ? (
        <ErrorState error={attendance.error} onRetry={() => void attendance.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No attendance recorded"
          message="Record the hours volunteers gave to this activity."
        />
      ) : (
        <AttendanceTable rows={rows} showActivity={false} />
      )}
    </Card>
  );
}

function RecordForm({ activity, onClose }: { activity: ActivityWithProgram; onClose: () => void }) {
  const create = useCreateAttendance();
  const roster = useActivityAssignments(activity.activity_id);

  const sessions = useActivitySessions(activity.activity_id);
  const availableSessions = sessions.data ?? [];
  const onlySession = availableSessions.length === 1 ? availableSessions[0]!.session_id : '';
  const [sessionId, setSessionId] = useState('');
  const chosenSession = sessionId || onlySession;

  const [volunteerId, setVolunteerId] = useState('');
  const [search, setSearch] = useState('');
  const [date, setDate] = useState(activity.start_date);
  /*
   * Pre-filled from the activity (migration 044), because the figure is the
   * same for almost everybody at the same session and retyping it per volunteer
   * is how a 90 becomes a 9.
   *
   * A starting value, not a lock: the box is ordinary and staff change it for
   * whoever left early. Activities with no default open at zero, which is the
   * behaviour every activity had before this.
   */
  const [minutes, setMinutes] = useState(activity.default_duration_minutes ?? 0);
  const [headcount, setHeadcount] = useState(1);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  /** Whatever the API has asked so far — none, one, or both of D3 and D4. */
  const confirmations = useConfirmations();

  // Anyone still expected at this activity, offered first because they are who
  // staff are usually recording.
  const assigned = (roster.data ?? []).filter((row) => row.assignment_status !== 'CANCELLED');

  // A search only matters when recording for someone who was not assigned.
  const term = search.trim();
  const others = useVolunteers(
    { search: term, status: 'ACTIVE', page_size: 10 },
    { enabled: term.length >= 2 }
  );

  const assignedIds = new Set(assigned.map((row) => row.volunteer_id));
  const extraOptions = (others.data?.items ?? [])
    .filter((volunteer) => !assignedIds.has(volunteer.volunteer_id))
    .map((volunteer) => ({
      value: volunteer.volunteer_id,
      label: `${[volunteer.first_name, volunteer.last_name].filter(Boolean).join(' ')} (${volunteer.volunteer_code}) — not assigned`,
    }));

  // A group record's headcount is asked for; an individual's is always one.
  // The roster carries `volunteer_category`; a searched volunteer carries
  // `category`. Same fact, two joins, so it is read from whichever is present.
  const chosen: { category?: string; volunteer_category?: string } | undefined =
    assigned.find((row) => row.volunteer_id === volunteerId) ??
    (others.data?.items ?? []).find((row) => row.volunteer_id === volunteerId);
  const chosenCategory = chosen?.category ?? chosen?.volunteer_category;
  const isGroup = Boolean(
    chosenCategory && isGroupCategory(chosenCategory as Parameters<typeof isGroupCategory>[0])
  );

  const options = [
    ...assigned.map((row) => ({
      value: row.volunteer_id,
      label: `${row.volunteer_name} (${row.volunteer_code})`,
    })),
    ...extraOptions,
  ];

  /**
   * `confirmed` is what staff clicking "record it anyway" means: send every flag
   * the warnings have earned. A first attempt sends none, so nothing is ever
   * waved through unseen.
   */
  const submit = (confirmed: boolean) => {
    const problems: Record<string, string> = {};
    if (!volunteerId) problems.volunteer_id = 'Choose a volunteer.';
    if (!chosenSession) problems.session_id = 'Choose which session these hours were given to.';
    if (!date) problems.attendance_date = 'When did they attend?';
    if (date && date > new Date().toISOString().slice(0, 10)) {
      problems.attendance_date = 'Attendance cannot be recorded for a date in the future.';
    }
    if (Object.keys(problems).length > 0) {
      setErrors(problems);
      return;
    }

    const body: CreateAttendanceBody = {
      volunteer_id: volunteerId,
      session_id: chosenSession,
      attendance_date: date,
      duration_minutes: minutes,
      // Only when it means something. Sending 1 for an individual would be
      // harmless but noisy in the audit trail.
      ...(headcount > 1 ? { participant_count: headcount } : {}),
      notes: notes.trim() || null,
      ...(confirmed ? confirmations.flags : {}),
    };

    create.mutate(body, {
      onSuccess: () => {
        setVolunteerId('');
        setMinutes(0);
        setHeadcount(1);
        setNotes('');
        confirmations.reset();
        onClose();
      },
      onError: (error) => {
        // D3 and D4 are questions, not failures. `remember` keeps them and
        // returns false for anything else, which falls through to the error line.
        confirmations.remember(error);
      },
    });
  };

  const asking = confirmations.asked.length > 0;
  const generalError = create.isError && !asking ? (create.error as Error).message : null;

  return (
    <div className={styles.form}>
      {generalError && (
        <p className={styles.error} role="alert">
          {generalError}
        </p>
      )}

      <ConfirmWarning
        questions={confirmations.asked}
        confirmLabel="Record it anyway"
        pending={create.isPending}
        onConfirm={() => submit(true)}
        onCancel={() => confirmations.reset()}
      />

      <fieldset className={styles.fields} disabled={create.isPending || asking}>
        {availableSessions.length > 1 && (
          <SelectInput
            label="Session"
            required
            placeholder="Which session were these hours given to?"
            options={availableSessions.map((p) => ({
              value: p.session_id,
              label: sessionLabel(p),
            }))}
            value={chosenSession}
            error={errors.session_id}
            onChange={(event) => {
              setSessionId(event.target.value);
              setErrors((current) => ({ ...current, session_id: '' }));
            }}
          />
        )}

        <SelectInput
          label="Volunteer"
          required
          placeholder="Choose someone"
          options={options}
          value={volunteerId}
          error={errors.volunteer_id}
          onChange={(event) => {
            setVolunteerId(event.target.value);
            setErrors((current) => ({ ...current, volunteer_id: '' }));
          }}
        />

        <TextInput
          label="Search for someone not assigned"
          placeholder="Name, code or city"
          value={search}
          error={
            /*
              A failed search would otherwise be invisible: the picker simply
              offers no extra names, which reads as "that person is not in the
              system". Staff would then record the hours against the wrong
              volunteer, or go and create a duplicate.
            */
            others.isError ? 'The search did not complete — nobody could be looked up.' : undefined
          }
          onChange={(event) => setSearch(event.target.value)}
        />

        <div className={styles.row}>
          <TextInput
            label="Date attended"
            type="date"
            required
            max={new Date().toISOString().slice(0, 10)}
            value={date}
            error={errors.attendance_date}
            onChange={(event) => {
              setDate(event.target.value);
              setErrors((current) => ({ ...current, attendance_date: '' }));
            }}
          />
          <TextInput
            label="Notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>

        {/*
          Only for a group record, and that is the whole point of asking the
          volunteer's category first: a headcount typed against one person credits
          them with several times the hours they worked, in the table §4.29 makes
          the basis of reporting. The API refuses it, but the field should not be
          there to be typed into.
        */}
        {isGroup && (
          <TextInput
            label="How many people came?"
            type="number"
            min={1}
            required
            value={String(headcount)}
            onChange={(event) => setHeadcount(Number(event.target.value) || 1)}
          />
        )}

        <DurationInput
          label="How long were they there?"
          required
          value={minutes}
          error={errors.duration_minutes}
          onChange={(value) => {
            setMinutes(value);
            setErrors((current) => ({ ...current, duration_minutes: '' }));
          }}
        />
      </fieldset>

      {!asking && (
        <div className={styles.actions}>
          <Button variant="primary" loading={create.isPending} onClick={() => submit(false)}>
            Record attendance
          </Button>
          <Button onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
